import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import KpiCard from "@/components/KpiCard";
import api from "@/lib/api";
import { fmtL, fmtNum, fmtDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import RtcPanel from "@/pages/_rtc/RtcPanel";
import ComercialDashboard from "@/pages/_dashboard/ComercialDashboard";
import Comunicaciones from "@/components/Comunicaciones";
import {
  CurrencyDollarIcon, ReceiptIcon, BankIcon, PackageIcon, WarningCircleIcon,
  ArrowRightIcon, CalendarBlankIcon, BellRingingIcon,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [loading, setLoading] = useState(true);

  const esRtc = Boolean(user?.effective?.alcance_regional);
  // Delegados y RTC: Dashboard comercial (sin cuentas por cobrar ni valoración de inventario)
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const comercial = !esAdmin && (esRtc || (user?.effective?.roles || []).includes("delegado"));
  useEffect(() => {
    if (comercial) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/dashboard/kpis");
        setK(data);
      } catch { /* sin acceso a indicadores generales */ } finally { setLoading(false); }
    })();
  }, [comercial]);

  const subtitle = new Date().toLocaleDateString("es-HN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <Layout title={`Hola, ${user?.name?.split(" ")[0] || ""}`} subtitle={subtitle}>
      {/* Avisos y comunicados oficiales: para todos los perfiles */}
      <div className="mb-6"><Comunicaciones /></div>
      {comercial ? (
        <div className="space-y-6">
          {esRtc && <RtcPanel />}
          <ComercialDashboard />
        </div>
      ) : loading ? (
        <div className="text-text-muted">Cargando indicadores…</div>
      ) : !k ? null : (
        <div className="space-y-5">
          {/* ============ KPI Row (4 cards grandes) ============ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              testId="kpi-ventas-dia"
              label="Ventas del día"
              value={fmtL(k.ventas_dia)}
              sub={`${k.facturas_dia} facturas hoy`}
              icon={CurrencyDollarIcon}
              accent="green"
              trend={null}
            />
            <KpiCard
              testId="kpi-ventas-mes"
              label="Ventas del mes"
              value={fmtL(k.ventas_mes)}
              sub={`${k.facturas_mes} facturas`}
              icon={ReceiptIcon}
              accent="yellow"
              trend={12.4}
            />
            {k.cxc_total !== undefined && <KpiCard
              testId="kpi-cxc"
              label="Cuentas por cobrar"
              value={fmtL(k.cxc_total)}
              sub="Saldo pendiente"
              icon={BankIcon}
              accent="blue"
            />}
            {k.inventario_valorizado !== undefined && <KpiCard
              testId="kpi-inventario"
              label="Inventario valorizado"
              value={fmtL(k.inventario_valorizado)}
              sub={`${k.productos_proximos_vencer} próx. a vencer`}
              icon={PackageIcon}
              accent="green"
            />}
          </div>

          {/* ============ Charts row: 2 cards + 1 promo verde grande ============ */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Ventas 7 días (col-span 5) */}
            <div className="lg:col-span-5 bg-card border border-border/60 rounded-2xl p-5" data-testid="chart-ventas-7-dias">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Tendencia</div>
                  <h3 className="font-heading text-lg font-medium text-text-primary mt-1">Ventas últimos 7 días</h3>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Total período</div>
                  <div className="font-heading text-xl text-agro-green font-semibold">
                    {fmtL(k.ventas_7_dias.reduce((s, d) => s + d.total, 0))}
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={k.ventas_7_dias} margin={{ left: -10, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#02BF44" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#02BF44" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222D26" vertical={false} />
                  <XAxis dataKey="fecha" stroke="#68756D" fontSize={11} tickFormatter={(d) => new Date(d).toLocaleDateString("es-HN", { day: "2-digit", month: "short" })} />
                  <YAxis stroke="#68756D" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    contentStyle={{ background: "#111612", border: "1px solid #222D26", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#A1ADA5" }}
                    formatter={(v) => [fmtL(v), "Ventas"]}
                    labelFormatter={(d) => fmtDate(d)}
                  />
                  <Area type="monotone" dataKey="total" stroke="#02BF44" strokeWidth={2.4} fill="url(#grad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Ventas por región (col-span 4) */}
            <div className="lg:col-span-4 bg-card border border-border/60 rounded-2xl p-5" data-testid="chart-region">
              <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Distribución</div>
              <h3 className="font-heading text-lg font-medium text-text-primary mt-1 mb-4">Ventas por región</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={k.ventas_por_region.slice(0, 6)} layout="vertical" margin={{ left: 0, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222D26" horizontal={false} />
                  <XAxis type="number" stroke="#68756D" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <YAxis type="category" dataKey="_id" stroke="#A1ADA5" fontSize={11} width={90} />
                  <Tooltip
                    contentStyle={{ background: "#111612", border: "1px solid #222D26", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => fmtL(v)}
                  />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                    {k.ventas_por_region.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={i === 0 ? "#02BF44" : i === 1 ? "#FAD22C" : `rgba(2,191,68,${0.8 - i * 0.12})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* CARD PROMO VERDE GRANDE: Próximos vencimientos (col-span 3) */}
            {k.facturas_por_vencer !== undefined && <UpcomingDueCard items={k.facturas_por_vencer || []} onClick={() => navigate("/cxc")} />}
          </div>

          {/* ============ Tablas ============ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border/60 rounded-2xl overflow-hidden" data-testid="top-clientes">
              <div className="p-5 border-b border-border/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Top 5</div>
                  <h3 className="font-heading text-lg font-medium text-text-primary mt-1">Clientes del mes</h3>
                </div>
                <span className="text-xs text-agro-green font-medium">{k.top_clientes.length} clientes</span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted border-b border-border/40">
                    <th className="p-3 pl-5">Cliente</th>
                    <th className="p-3 text-right">Deals</th>
                    <th className="p-3 pr-5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {k.top_clientes.map((c, i) => (
                    <tr key={c._id || i} className="border-t border-border/30 hover:bg-white/[0.02]">
                      <td className="p-3 pl-5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-agro-green to-agro-yellow flex items-center justify-center text-black text-[10px] font-bold flex-shrink-0">
                            {(c.nombre || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase()}
                          </div>
                          <div className="text-sm text-text-primary truncate">{c.nombre}</div>
                        </div>
                      </td>
                      <td className="p-3 text-right text-sm text-text-secondary">{c.facturas || 1}</td>
                      <td className="p-3 pr-5 text-right text-sm font-medium text-agro-green">{fmtL(c.total)}</td>
                    </tr>
                  ))}
                  {k.top_clientes.length === 0 && (
                    <tr><td colSpan="3" className="p-8 text-center text-text-muted text-sm">Sin datos en este mes</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-card border border-border/60 rounded-2xl overflow-hidden" data-testid="top-productos">
              <div className="p-5 border-b border-border/60 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Top 5</div>
                  <h3 className="font-heading text-lg font-medium text-text-primary mt-1">Productos más vendidos</h3>
                </div>
                <span className="text-xs text-agro-yellow font-medium">Mes en curso</span>
              </div>
              <table className="w-full">
                <tbody>
                  {k.top_productos.map((p, i) => (
                    <tr key={p._id || i} className="border-t border-border/30 hover:bg-white/[0.02]">
                      <td className="p-3 pl-5 w-8 text-text-muted font-mono text-xs">#{i + 1}</td>
                      <td className="p-3">
                        <div className="text-sm text-text-primary">{p.descripcion}</div>
                        <div className="text-[11px] text-text-muted mt-0.5">{fmtNum(p.cantidad)} unidades</div>
                      </td>
                      <td className="p-3 pr-5 text-sm text-right font-medium text-agro-green">{fmtL(p.total)}</td>
                    </tr>
                  ))}
                  {k.top_productos.length === 0 && (
                    <tr><td colSpan="3" className="p-8 text-center text-text-muted text-sm">Sin ventas en este mes</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabla completa de Por Vencer si hay datos */}
          {(k.facturas_por_vencer || []).length > 0 && (
            <div className="bg-card border border-agro-yellow/25 rounded-2xl overflow-hidden" data-testid="facturas-por-vencer">
              <div className="p-5 border-b border-border/60 flex items-center gap-3">
                <WarningCircleIcon size={20} className="text-agro-yellow" weight="duotone" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-agro-yellow">Atención</div>
                  <h3 className="font-heading text-lg font-medium text-text-primary mt-1">Facturas por vencer (próximos 7 días)</h3>
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted border-b border-border/40">
                    <th className="p-3 pl-5">Factura</th>
                    <th className="p-3">Cliente</th>
                    <th className="p-3">Vencimiento</th>
                    <th className="p-3 text-right pr-5">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {k.facturas_por_vencer.map((f) => (
                    <tr key={f.id} className="border-t border-border/30 hover:bg-white/[0.02]">
                      <td className="p-3 pl-5 text-sm font-mono text-text-primary">{f.numero}</td>
                      <td className="p-3 text-sm">{f.cliente_nombre}</td>
                      <td className="p-3 text-sm text-text-secondary">{fmtDate(f.fecha_vencimiento)}</td>
                      <td className="p-3 pr-5 text-sm text-right font-medium text-agro-yellow">{fmtL(f.saldo_pendiente)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

/**
 * Card promocional verde grande inspirada en la imagen del cliente.
 * Reemplaza el "$30 / mes / usuario" con "Próximos vencimientos" de AGROPORT.
 */
function UpcomingDueCard({ items, onClick }) {
  const top3 = items.slice(0, 3);
  const totalSaldo = items.reduce((s, f) => s + (f.saldo_pendiente || 0), 0);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="card-proximos-vencimientos"
      className="promo-card lg:col-span-3 relative rounded-2xl overflow-hidden text-left p-5 group transition-all duration-300 hover:scale-[1.01]"
    >
      {/* Glow patterns */}
      <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
           style={{
             backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.5), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.25), transparent 50%)",
           }}
      />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-black/15 backdrop-blur-sm rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 ring-1 ring-black/20">
            <BellRingingIcon size={12} weight="fill" className="text-black" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-black">Atención</span>
          </div>
        </div>

        <div className="flex items-baseline gap-2 mb-1">
          <div className="font-heading text-4xl lg:text-5xl font-bold text-black leading-none tracking-tight">
            {items.length}
          </div>
          <div className="text-sm font-medium text-black/75">
            {items.length === 1 ? "factura" : "facturas"}
          </div>
        </div>
        <div className="text-sm text-black/85 mb-4">
          Por cobrar en los próximos 7 días
        </div>

        <div className="text-[10px] uppercase tracking-wider text-black/65 mb-1">Saldo en riesgo</div>
        <div className="font-heading text-2xl font-bold text-black mb-4">{fmtL(totalSaldo)}</div>

        {top3.length > 0 && (
          <div className="space-y-1.5 mb-4">
            {top3.map((f) => (
              <div key={f.id} className="flex items-center justify-between text-xs bg-black/10 backdrop-blur-sm rounded-md px-2.5 py-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <CalendarBlankIcon size={11} weight="fill" className="text-black/70 flex-shrink-0" />
                  <span className="text-black font-medium truncate">{f.cliente_nombre}</span>
                </div>
                <span className="text-black/85 font-semibold flex-shrink-0 ml-2">{fmtL(f.saldo_pendiente)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="inline-flex items-center gap-1.5 bg-black promo-card-cta rounded-full px-4 py-2 text-xs font-semibold group-hover:gap-2.5 transition-all">
          Ver cobranza
          <ArrowRightIcon size={12} weight="bold" />
        </div>
      </div>
    </button>
  );
}
